import * as Rest from "../Generated/RestClient";
import {parseUrl, Url} from "../../../Intercom.ReactUI/Utils/Url";
import {Poller, PollingModel} from "../Models/PollingModel";
import downloadBlob from "../../../Intercom.ReactUI/Utils/downloadBlob";

enum Method {
    GET,
    PUT,
    DELETE,
    POST
}

interface KeyInfo {
    name: string;
    value: string;
}

/**
 *  repository type from Kudu
 https://github.com/projectkudu/kudu/blob/2ea953e532d0a44afe291dc4b9e370df9fc51686/Kudu.Contracts/SourceControl/RepositoryType.cs
 */
export enum RepositoryType {
    None,
    Git,
    Mercurial
}

export interface ScmInfo {
    Type: RepositoryType;
    GitUrl: string;
}

export class KuduClient {
    public scmUri: Url;
    private statusPoller: Poller<any>;
    private masterKey;
    private authz;

    public static createFromScmAuthorization(authz: Rest.ScmAuthorization): KuduClient {
        const kuduClient = new KuduClient();
        kuduClient.authz = authz;
        kuduClient.scmUri = parseUrl(authz.Url);
        return kuduClient;
    }

    public static createFromArmToken(kuduUri: string, armToken: string): KuduClient {
        if (!kuduUri) {
            throw new Error("kuduUri must be defined");
        }
        if (!armToken) {
            throw new Error("armToken must be defined");
        }
        const kuduClient = new KuduClient();
        kuduClient.scmUri = parseUrl(kuduUri);
        kuduClient.authz = armToken;
        return kuduClient;
    }

    /**
     * For a bot get the kudu uri (ie https://<site>.scm.azurewebsites.net).
     */
    public static getKuduSiteName(bot: Rest.BotService): string {
        if (!bot) {
            throw new Error("bot must be defined");
        }
        if (!bot.Endpoint) {
            throw new Error("bot must have an Endpoint");
        }

        const siteUrl: Url = parseUrl(bot.Endpoint);
        const siteName = siteUrl.hostname.substring(0, siteUrl.hostname.indexOf("."));
        return `https://${siteName}.scm.azurewebsites.net`;
    }

    private get rootUri() : Url {
        return  parseUrl(this.scmUri.format());
    }

    public command(script: string, folder: string) : JQueryPromise<boolean> {
        let uri = this.rootUri;
        uri.path = '/api/command';
        return this.request(uri.format(), Method.POST, true, {command: script, dir: folder});
    }

    public get(url: string): JQueryPromise<string | Rest.VfsNodeInfo[]> {
        const isFolder = url[url.length - 1] === '/';
        return isFolder ? this.request<Rest.VfsNodeInfo[]>(url, Method.GET, isFolder) :
            this.request<string>(url, Method.GET, isFolder);
    }

    public dir(path: string): JQueryPromise<Rest.VfsNodeInfo[]> {
        if (path.length && path[path.length-1] !== '/') {
            path = `${path}/`;
        }
        let uri = this.rootUri;
        uri.path = `/api/vfs/${path}`;
        return this.get(uri.format());
    }

    public get scmInfo(): JQueryPromise<ScmInfo> {
        let uri = this.rootUri;
        uri.path ='/api/scm/info'
        return this.request(uri.format(), Method.GET, true);
    }

    public get settings(): JQueryPromise<{[key:string]: string}> {
        let uri = this.rootUri;
        uri.path = `/api/settings`;
        return this.request(uri.format(), Method.GET, true);
    }

    public put(url: string, content: string): JQueryPromise<boolean> {
        return this.request<boolean>(url, Method.PUT, false, content);
    }

    public del(url: string): JQueryPromise<boolean> {
        return this.request<boolean>(url, Method.DELETE);
    }

    public download(path: string) : JQueryPromise<Blob> {
        let uri = this.rootUri;
        uri.path = `/api/vfs/${path}`;

        return downloadBlob({
            url: uri.format(),
            type: 'GET',
            headers: {
                'Authorization': this.getAuthHeader(),
                'If-Match': '*',
                'Accept': 'application/zip, application/octet-stream'
            },
            beforeSend: () => Rest.showNetworkIcon(true),
        }).always(() => {            
            Rest.showNetworkIcon(false);
        });        
    }

    public zip(path : string) : JQueryPromise<Blob> {
        let uri = this.rootUri;
        uri.path = `/api/zip/${path}`;

        return downloadBlob({
            url: uri.format(),
            type: 'GET',
            headers: {
                'Authorization': this.getAuthHeader(),
                'If-Match': '*',
                'Accept': 'application/zip, application/octet-stream'
            },
            beforeSend: () => Rest.showNetworkIcon(true),
        }).always(() => {            
            Rest.showNetworkIcon(false);
        });
    }

    public logStream(functionName: string, onData: (log: string) => void): Poller<string> {
        let logstreamUri = this.rootUri;
        logstreamUri.path = `/api/logstream/application/functions/function/${functionName}`;
        let xhr = new XMLHttpRequest();
        xhr.open('GET', logstreamUri.format(), true);
        xhr.setRequestHeader('Authorization', this.getAuthHeader());
        xhr.setRequestHeader('FunctionsPortal', '1');
        xhr.send(null);
        let prevLength = xhr.responseText ? xhr.responseText.length : 0;
        let poller = new Poller(null,
            () => {
                let deferred = $.Deferred();
                const curLength = xhr.responseText.length;
                if (curLength > prevLength) {
                    deferred.resolve(xhr.responseText.substr(prevLength));
                    prevLength = curLength;
                } else {
                    if (xhr.readyState == XMLHttpRequest.DONE) {
                        poller.stop();
                    }
                    deferred.resolve(null);
                }
                return deferred.promise();
            },
            onData);
        // log stream requires we keep a connection to the function host/admin/host/status 
        // open,  create a poller here to do so
        if (!this.statusPoller) {
            this.pingHostStatus();
            this.statusPoller = new Poller<any>(
                { defaultInterval: 60000, maxPendingPollRequests: 1 },
                _.bind(this.pingHostStatus, this), d => { });
            this.statusPoller.start();
        }

        return poller; 
    }

    public get publishPassword() : string {
        return this.authz.Password;
    }

    private getFunctionToken(): JQueryPromise<string> {
        let tokenUri = this.rootUri;
        tokenUri.path = '/api/functions/admin/token';
        return this.request<string>(tokenUri.format(), Method.GET, true);
    }

    private getMasterKey(): JQueryPromise<string> {
        if (this.masterKey) {
            let deferred = $.Deferred();
            deferred.resolve(this.masterKey);
            return deferred;
        } else {
            return this.getFunctionToken().then(token => {
                let keyUrl = this.rootUri;
                keyUrl.hostname = keyUrl.hostname.replace(".scm", "");
                keyUrl.path = "/admin/host/systemkeys/_master";
                return this.request<KeyInfo>(keyUrl.format(), Method.GET, true, null, token);
            }).then(keyInfo => {
                this.masterKey = keyInfo.value;
                return $.Deferred().resolve(this.masterKey);
            });
        }
    }

    private pingHostStatus(): JQueryPromise<any> {
        return this.getMasterKey().then((key: string) => {
            let statusUri = this.rootUri;
            statusUri.hostname = statusUri.hostname.replace(".scm", "");
            statusUri.path = '/admin/host/status';
            return $.ajax({
                url: statusUri.format(),
                type: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json,*/*",
                    "x-functions-key": key,
                }
            });
        });
    }

    private request<T>(url: string, method: Method,  isFolder?: boolean, data?: any, token?: string): JQueryPromise<T> {
        this.validateUrl(url);
        const postingData = data && method == Method.POST;
        return $.ajax({
            url: url,
            type: Method[method] as string,
            headers: {
                'Authorization': this.getAuthHeader(token),
                'If-Match': '*',
                'Accept': 'application/json,*/*'
            },
            dataType: isFolder ? 'json' : 'text',
            beforeSend: () => Rest.showNetworkIcon(true),
            data: postingData ? JSON.stringify(data) : data,
            contentType: postingData ? 'application/json' : undefined
        }).always(() => Rest.showNetworkIcon(false));
    }

    private validateUrl(url: string) {
        const uri = parseUrl(url);
        if (!uri || uri.scheme != this.scmUri.scheme ||
            (uri.hostname != this.scmUri.hostname && 
             uri.hostname != this.scmUri.hostname.replace('.scm', ''))) {
            throw 'non conforming url';
        }
    }

    private getAuthHeader(token?: string): string {
        if (token) {
            return `Bearer ${token}`;
        }
        if (_.isString(this.authz)) {
            return `Bearer ${this.authz}`;
        } else if (_.isObject(this.authz)) {
            const authText = `${this.authz.UserName}:${this.authz.Password}`;
            return `Basic ${btoa(authText)}`;
        }
        throw "no authz info available";
    }
}



// WEBPACK FOOTER //
// ./Client/Services/KuduApi.ts